import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getFighters, getFighter, createFighter, updateFighter, deleteFighter, getAssessments, getFighterNotes, createFighterNote, trackFeatureUsage } from '../services/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorUtils';
import axios from 'axios';
import { 
  Plus, 
  Search, 
  Users,
  ArrowLeft,
  Edit,
  Trash2,
  ChevronRight,
  Trophy,
  Share2,
  Link as LinkIcon,
  Copy,
  Check,
  ExternalLink,
  X,
  MessageSquare,
  Send,
  User,
  Award,
  Swords,
  Dumbbell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { BeltTrackingCard } from '../components/BeltTracking';
import { CompetitionRecords } from '../components/CompetitionRecords';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  Radar, 
  ResponsiveContainer 
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const WEIGHT_CLASSES = [
  'Strawweight', 'Flyweight', 'Bantamweight', 'Featherweight', 
  'Lightweight', 'Welterweight', 'Middleweight', 'Light Heavyweight', 'Heavyweight'
];

const STANCES = ['Orthodox', 'Southpaw', 'Switch'];

const BJJ_BELT_RANKS = ['white', 'blue', 'purple', 'brown', 'black'];
const BJJ_STRIPES = [0, 1, 2, 3, 4];
const SPORT_TYPES = ['mma', 'bjj'];

// Fighters List Page
export const FightersListPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [fighters, setFighters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterWeight, setFilterWeight] = useState('all');

  useEffect(() => {
    loadFighters();
    trackFeatureUsage('fighters', 'view').catch(() => {});
  }, []);

  const loadFighters = async () => {
    try {
      const response = await getFighters();
      setFighters(response.data);
    } catch (error) {
      toast.error('Failed to load fighters');
    } finally {
      setLoading(false);
    }
  };

  const filteredFighters = fighters.filter(fighter => {
    const matchesSearch = fighter.name.toLowerCase().includes(search.toLowerCase()) ||
      (fighter.nickname && fighter.nickname.toLowerCase().includes(search.toLowerCase()));
    const matchesWeight = filterWeight === 'all' || fighter.weight_class === filterWeight;
    return matchesSearch && matchesWeight;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="fighters-list-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">Team Roster</p>
            <h1 className="font-heading text-4xl md:text-5xl text-white tracking-tight">
              FIGHT TEAM
            </h1>
            <p className="text-zinc-500 mt-2 font-body">{fighters.length} fighter{fighters.length !== 1 ? 's' : ''} on your roster</p>
          </div>
          {isAdmin && (
            <Button 
              onClick={() => navigate('/fighters/new')}
              className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider transition-all"
              data-testid="add-fighter-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Fighter
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <Input
              placeholder="Search fighters..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12 bg-ba-surface border-ba-border focus:border-red-600 font-body"
              data-testid="search-fighters-input"
            />
          </div>
          <Select value={filterWeight} onValueChange={setFilterWeight}>
            <SelectTrigger className="w-full sm:w-48 h-12 bg-ba-surface border-ba-border font-body" data-testid="weight-class-filter">
              <SelectValue placeholder="Weight Class" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Weight Classes</SelectItem>
              {WEIGHT_CLASSES.map(wc => (
                <SelectItem key={wc} value={wc}>{wc}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Fighters Grid */}
        {filteredFighters.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFighters.map((fighter, index) => (
              <motion.div
                key={fighter.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/fighters/${fighter.id}`}
                  className="block p-6 bg-zinc-900/50 border border-zinc-800 rounded-sm hover:border-red-600/50 transition-all group"
                  data-testid={`fighter-card-${fighter.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-zinc-800 rounded-full flex items-center justify-center group-hover:bg-red-600/20 transition-colors">
                        <Users className="w-7 h-7 text-zinc-400 group-hover:text-red-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-medium text-lg">{fighter.name}</h3>
                        {fighter.nickname && (
                          <p className="text-zinc-500 text-sm">"{fighter.nickname}"</p>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-red-500 transition-colors" />
                  </div>
                  <div className="mt-4 flex items-center gap-4">
                    <span className="text-xs px-2 py-1 bg-zinc-800 text-zinc-400 rounded-sm">
                      {fighter.weight_class}
                    </span>
                    <span className="text-zinc-400 text-sm tabular-nums">
                      {fighter.record_wins}-{fighter.record_losses}-{fighter.record_draws}
                    </span>
                  </div>
                  {fighter.stance && (
                    <p className="text-zinc-500 text-sm mt-2">{fighter.stance}</p>
                  )}
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-16 px-8 text-center bg-zinc-900/30 border border-zinc-800 rounded-sm"
          >
            <div className="w-16 h-16 bg-red-600/20 border border-red-600/30 rounded-sm flex items-center justify-center mb-6">
              <Users className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="font-heading text-2xl text-white uppercase tracking-tight mb-2">
              {search || filterWeight !== 'all' ? 'No Fighters Match' : 'Build Your Fight Team'}
            </h3>
            <p className="text-zinc-400 mb-6 max-w-md">
              {search || filterWeight !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Add your first fighter to start tracking their performance, camps, and fight preparation.'
              }
            </p>
            {isAdmin && !search && filterWeight === 'all' && (
              <Button 
                onClick={() => navigate('/fighters/new')}
                className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow uppercase font-heading tracking-wider px-6"
                data-testid="add-first-fighter-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Fighter
              </Button>
            )}
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Fighter Form Page (Create/Edit)
export const FighterFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isEdit = Boolean(id);
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    nickname: '',
    weight_class: '',
    height: '',
    reach: '',
    stance: '',
    date_of_birth: '',
    record_wins: 0,
    record_losses: 0,
    record_draws: 0,
    bjj_belt: 'white',
    bjj_stripes: 0,
    sport_types: ['mma']  // Default to MMA, can be ['mma'], ['bjj'], or ['mma', 'bjj']
  });

  useEffect(() => {
    if (isEdit) {
      loadFighter();
    }
  }, [id]);

  const loadFighter = async () => {
    try {
      const response = await getFighter(id);
      setFormData(response.data);
    } catch (error) {
      toast.error('Fighter not found');
      navigate('/fighters');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await updateFighter(id, formData);
        toast.success('Fighter updated');
      } else {
        await createFighter(formData);
        toast.success('Fighter created');
      }
      navigate('/fighters');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save fighter'));
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    navigate('/fighters');
    return null;
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="fighter-form-page">
        <Button
          variant="ghost"
          onClick={() => navigate('/fighters')}
          className="text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Fighters
        </Button>

        <h1 className="font-heading text-3xl font-bold text-white uppercase tracking-tight mb-8">
          {isEdit ? 'Edit Fighter' : 'Add Fighter'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sport Type Selection */}
          <div className="space-y-3">
            <Label className="text-zinc-300">Sport Type(s) *</Label>
            <p className="text-xs text-zinc-500">Select all disciplines this fighter competes in (can select both)</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  const types = formData.sport_types || [];
                  if (types.includes('mma')) {
                    setFormData({ ...formData, sport_types: types.filter(t => t !== 'mma') });
                  } else {
                    setFormData({ ...formData, sport_types: [...types, 'mma'] });
                  }
                }}
                className={`flex-1 py-3 px-4 rounded-sm border transition-all flex items-center justify-center gap-2 ${
                  (formData.sport_types || []).includes('mma')
                    ? 'border-red-600 bg-red-600/10 text-white'
                    : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
                }`}
                data-testid="fighter-sport-mma-toggle"
              >
                <Swords className="w-5 h-5" />
                MMA
                {(formData.sport_types || []).includes('mma') && (
                  <span className="ml-1 text-xs bg-red-600/30 px-1.5 py-0.5 rounded">✓</span>
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  const types = formData.sport_types || [];
                  if (types.includes('bjj')) {
                    setFormData({ ...formData, sport_types: types.filter(t => t !== 'bjj') });
                  } else {
                    setFormData({ ...formData, sport_types: [...types, 'bjj'] });
                  }
                }}
                className={`flex-1 py-3 px-4 rounded-sm border transition-all flex items-center justify-center gap-2 ${
                  (formData.sport_types || []).includes('bjj')
                    ? 'border-blue-600 bg-blue-600/10 text-white'
                    : 'border-zinc-800 text-zinc-400 hover:border-zinc-700'
                }`}
                data-testid="fighter-sport-bjj-toggle"
              >
                <Award className="w-5 h-5" />
                BJJ
                {(formData.sport_types || []).includes('bjj') && (
                  <span className="ml-1 text-xs bg-blue-600/30 px-1.5 py-0.5 rounded">✓</span>
                )}
              </button>
            </div>
            {(formData.sport_types || []).length === 2 && (
              <div className="flex items-center gap-2 text-sm text-purple-400 bg-purple-600/10 p-3 rounded-sm border border-purple-600/30">
                <span className="text-lg">🏆</span>
                <span><strong>Multi-Sport Athlete!</strong> You can now run separate MMA and BJJ assessments for this fighter.</span>
              </div>
            )}
            {(formData.sport_types || []).length === 0 && (
              <p className="text-xs text-amber-500">⚠️ Please select at least one sport type</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-zinc-300">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
                required
                data-testid="fighter-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nickname" className="text-zinc-300">Nickname</Label>
              <Input
                id="nickname"
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                placeholder="The Beast"
                className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
                data-testid="fighter-nickname-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="weight_class" className="text-zinc-300">Weight Class *</Label>
              <Select 
                value={formData.weight_class} 
                onValueChange={(value) => setFormData({ ...formData, weight_class: value })}
              >
                <SelectTrigger className="h-12 bg-zinc-900 border-zinc-800" data-testid="fighter-weight-class-select">
                  <SelectValue placeholder="Select weight class" />
                </SelectTrigger>
                <SelectContent>
                  {WEIGHT_CLASSES.map(wc => (
                    <SelectItem key={wc} value={wc}>{wc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="stance" className="text-zinc-300">Stance</Label>
              <Select 
                value={formData.stance} 
                onValueChange={(value) => setFormData({ ...formData, stance: value })}
              >
                <SelectTrigger className="h-12 bg-zinc-900 border-zinc-800" data-testid="fighter-stance-select">
                  <SelectValue placeholder="Select stance" />
                </SelectTrigger>
                <SelectContent>
                  {STANCES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="height" className="text-zinc-300">Height</Label>
              <Input
                id="height"
                value={formData.height}
                onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                placeholder="5'10"
                className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
                data-testid="fighter-height-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reach" className="text-zinc-300">Reach</Label>
              <Input
                id="reach"
                value={formData.reach}
                onChange={(e) => setFormData({ ...formData, reach: e.target.value })}
                placeholder="72 inches"
                className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
                data-testid="fighter-reach-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date_of_birth" className="text-zinc-300">Date of Birth</Label>
              <Input
                id="date_of_birth"
                type="text"
                placeholder="YYYY-MM-DD"
                value={formData.date_of_birth}
                onChange={(e) => {
                  // Allow typing directly in YYYY-MM-DD format
                  let value = e.target.value;
                  // Auto-format: insert dashes after year and month
                  value = value.replace(/[^0-9-]/g, '');
                  if (value.length === 4 && !value.includes('-')) {
                    value = value + '-';
                  } else if (value.length === 7 && value.split('-').length === 2) {
                    value = value + '-';
                  }
                  // Limit to 10 characters (YYYY-MM-DD)
                  if (value.length <= 10) {
                    setFormData({ ...formData, date_of_birth: value });
                  }
                }}
                className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600"
                data-testid="fighter-dob-input"
              />
              <p className="text-xs text-zinc-500">Format: YYYY-MM-DD (e.g., 1995-03-15)</p>
            </div>
          </div>

          {/* BJJ Belt Rank Section */}
          <div className="border-t border-zinc-800 pt-6">
            <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight mb-4 flex items-center gap-2">
              <span className="w-6 h-3 bg-gradient-to-r from-yellow-600 to-yellow-500 rounded-sm" />
              BJJ Belt Rank
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="bjj_belt" className="text-zinc-300">Belt Color</Label>
                <Select 
                  value={formData.bjj_belt || 'white'} 
                  onValueChange={(value) => setFormData({ ...formData, bjj_belt: value })}
                >
                  <SelectTrigger className="h-12 bg-zinc-900 border-zinc-800" data-testid="fighter-belt-select">
                    <SelectValue placeholder="Select belt" />
                  </SelectTrigger>
                  <SelectContent>
                    {BJJ_BELT_RANKS.map(belt => (
                      <SelectItem key={belt} value={belt} className="capitalize">
                        <div className="flex items-center gap-2">
                          <span className={`w-4 h-2 rounded-sm ${
                            belt === 'white' ? 'bg-gray-100 border border-gray-300' :
                            belt === 'blue' ? 'bg-blue-600' :
                            belt === 'purple' ? 'bg-purple-600' :
                            belt === 'brown' ? 'bg-amber-800' :
                            'bg-zinc-900 border border-red-600'
                          }`} />
                          <span className="capitalize">{belt}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bjj_stripes" className="text-zinc-300">Stripes (0-4)</Label>
                <Select 
                  value={String(formData.bjj_stripes || 0)} 
                  onValueChange={(value) => setFormData({ ...formData, bjj_stripes: parseInt(value) })}
                >
                  <SelectTrigger className="h-12 bg-zinc-900 border-zinc-800" data-testid="fighter-stripes-select">
                    <SelectValue placeholder="Select stripes" />
                  </SelectTrigger>
                  <SelectContent>
                    {BJJ_STRIPES.map(num => (
                      <SelectItem key={num} value={String(num)}>
                        {num === 0 ? 'No stripes' : `${num} stripe${num > 1 ? 's' : ''}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Visual Belt Preview */}
            <div className="mt-4 p-4 bg-zinc-800/50 rounded-sm">
              <p className="text-xs text-zinc-500 mb-2">Preview</p>
              <div className="flex items-center gap-4">
                <div className={`h-6 w-32 rounded-sm relative shadow-lg ${
                  formData.bjj_belt === 'white' ? 'bg-gray-100 border-2 border-gray-300' :
                  formData.bjj_belt === 'blue' ? 'bg-blue-600 border-2 border-blue-400' :
                  formData.bjj_belt === 'purple' ? 'bg-purple-600 border-2 border-purple-400' :
                  formData.bjj_belt === 'brown' ? 'bg-amber-800 border-2 border-amber-600' :
                  'bg-zinc-900 border-2 border-red-600'
                }`}>
                  <div className="absolute right-2 top-0 bottom-0 w-4 bg-zinc-900 flex items-center justify-center gap-0.5">
                    {[...Array(formData.bjj_stripes || 0)].map((_, i) => (
                      <div key={i} className="w-0.5 h-3/4 bg-white" />
                    ))}
                  </div>
                </div>
                <span className="text-zinc-300 capitalize">
                  {formData.bjj_belt || 'White'} Belt {formData.bjj_stripes > 0 ? `• ${formData.bjj_stripes} stripe${formData.bjj_stripes > 1 ? 's' : ''}` : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Fight Record Section */}
          <div className="border-t border-zinc-800 pt-6">
            <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight mb-4">
              Fight Record
            </h3>
            <div className="grid grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="wins" className="text-zinc-300">Wins</Label>
                <Input
                  id="wins"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.record_wins === 0 ? '' : formData.record_wins}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d+$/.test(val)) {
                      setFormData({ ...formData, record_wins: val === '' ? 0 : parseInt(val, 10) });
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '') {
                      setFormData({ ...formData, record_wins: 0 });
                    }
                  }}
                  placeholder="0"
                  className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 text-green-500"
                  data-testid="fighter-wins-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="losses" className="text-zinc-300">Losses</Label>
                <Input
                  id="losses"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.record_losses === 0 ? '' : formData.record_losses}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d+$/.test(val)) {
                      setFormData({ ...formData, record_losses: val === '' ? 0 : parseInt(val, 10) });
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '') {
                      setFormData({ ...formData, record_losses: 0 });
                    }
                  }}
                  placeholder="0"
                  className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 text-red-500"
                  data-testid="fighter-losses-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="draws" className="text-zinc-300">Draws</Label>
                <Input
                  id="draws"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={formData.record_draws === 0 ? '' : formData.record_draws}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d+$/.test(val)) {
                      setFormData({ ...formData, record_draws: val === '' ? 0 : parseInt(val, 10) });
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === '') {
                      setFormData({ ...formData, record_draws: 0 });
                    }
                  }}
                  placeholder="0"
                  className="h-12 bg-zinc-900 border-zinc-800 focus:border-red-600 text-zinc-400"
                  data-testid="fighter-draws-input"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/fighters')}
              className="flex-1 h-12 border-zinc-700 text-zinc-300 hover:bg-zinc-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-semibold"
              data-testid="save-fighter-btn"
            >
              {saving ? 'Saving...' : isEdit ? 'Update Fighter' : 'Create Fighter'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

// Fighter Detail Page
export const FighterDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  
  const [fighter, setFighter] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [fighterNotes, setFighterNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareStatus, setShareStatus] = useState({ share_enabled: false, share_token: null });
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // New note state
  const [newNote, setNewNote] = useState('');
  const [sendingNote, setSendingNote] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [fighterRes, assessmentsRes] = await Promise.all([
        getFighter(id),
        getAssessments(id)
      ]);
      setFighter(fighterRes.data);
      setAssessments(assessmentsRes.data);
      
      // Load fighter notes
      try {
        const notesRes = await getFighterNotes({ fighter_id: fighterRes.data.id });
        setFighterNotes(notesRes.data || []);
      } catch (e) {
        console.error('Failed to load fighter notes');
      }
      
      // Load share status
      try {
        const shareRes = await axios.get(`${API}/api/fighters/${id}/share-status`);
        setShareStatus(shareRes.data);
      } catch (e) {
        // Ignore - share status endpoint may not exist for older versions
      }
    } catch (error) {
      toast.error('Fighter not found');
      navigate('/fighters');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendNote = async () => {
    if (!newNote.trim()) return;
    setSendingNote(true);
    try {
      await createFighterNote({
        content: newNote.trim(),
        note_type: 'general',
        reference_id: fighter.id
      });
      toast.success('Note sent to fighter');
      setNewNote('');
      // Reload notes
      const notesRes = await getFighterNotes({ fighter_id: fighter.id });
      setFighterNotes(notesRes.data || []);
    } catch (error) {
      toast.error('Failed to send note');
    } finally {
      setSendingNote(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFighter(id);
      toast.success('Fighter deleted');
      navigate('/fighters');
    } catch (error) {
      toast.error('Failed to delete fighter');
    }
  };

  const handleCreateShareLink = async () => {
    setShareLoading(true);
    try {
      const response = await axios.post(`${API}/api/fighters/${id}/share`);
      setShareStatus({ share_enabled: true, share_token: response.data.share_token });
      toast.success('Share link created!');
      trackFeatureUsage('fighter_share', 'create').catch(() => {});
    } catch (error) {
      toast.error('Failed to create share link');
    } finally {
      setShareLoading(false);
    }
  };

  const handleDisableSharing = async () => {
    setShareLoading(true);
    try {
      await axios.delete(`${API}/api/fighters/${id}/share`);
      setShareStatus({ share_enabled: false, share_token: null });
      toast.success('Sharing disabled');
    } catch (error) {
      toast.error('Failed to disable sharing');
    } finally {
      setShareLoading(false);
    }
  };

  const getShareUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/fighter/${shareStatus.share_token}`;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getShareUrl());
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const getLatestAssessment = () => {
    if (!assessments.length) return null;
    const latest = assessments[0];
    return [
      { skill: 'Striking', value: latest.striking },
      { skill: 'Grappling', value: latest.grappling },
      { skill: 'Wrestling', value: latest.wrestling },
      { skill: 'Cardio', value: latest.cardio },
    ];
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const radarData = getLatestAssessment();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="fighter-detail-page">
        <Button
          variant="ghost"
          onClick={() => navigate('/fighters')}
          className="text-zinc-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Fighters
        </Button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 bg-zinc-800 rounded-full flex items-center justify-center">
              <Users className="w-12 h-12 text-zinc-400" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
                {fighter.name}
              </h1>
              {fighter.nickname && (
                <p className="text-xl text-zinc-500">"{fighter.nickname}"</p>
              )}
              <div className="flex items-center gap-4 mt-2">
                <span className="px-3 py-1 bg-zinc-800 text-zinc-300 text-sm rounded-sm">
                  {fighter.weight_class}
                </span>
                <span className="font-heading text-xl text-white tabular-nums">
                  {fighter.record_wins}-{fighter.record_losses}-{fighter.record_draws}
                </span>
              </div>
            </div>
          </div>
          {isAdmin && (
            <div className="flex flex-wrap gap-2 sm:gap-3">
              <Button
                onClick={() => setShareDialogOpen(true)}
                variant="outline"
                className="flex-1 sm:flex-none border-zinc-700 text-zinc-300 hover:bg-zinc-900"
                data-testid="share-fighter-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
              <Button
                onClick={() => navigate(`/fighters/${id}/edit`)}
                variant="outline"
                className="flex-1 sm:flex-none border-zinc-700 text-zinc-300 hover:bg-zinc-900"
                data-testid="edit-fighter-btn"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                onClick={() => setDeleteDialogOpen(true)}
                variant="outline"
                className="flex-1 sm:flex-none border-red-800 text-red-500 hover:bg-red-950"
                data-testid="delete-fighter-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Info Card */}
          <div className="lg:col-span-4 bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
            <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight mb-4">
              Fighter Info
            </h3>
            <div className="space-y-4">
              {fighter.height && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">Height</span>
                  <span className="text-white">{fighter.height}</span>
                </div>
              )}
              {fighter.reach && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">Reach</span>
                  <span className="text-white">{fighter.reach}</span>
                </div>
              )}
              {fighter.stance && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">Stance</span>
                  <span className="text-white">{fighter.stance}</span>
                </div>
              )}
              {fighter.date_of_birth && (
                <div className="flex justify-between">
                  <span className="text-zinc-500">Date of Birth</span>
                  <span className="text-white">{fighter.date_of_birth}</span>
                </div>
              )}
            </div>
          </div>

          {/* Skills Radar */}
          <div className="lg:col-span-8 bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-white uppercase tracking-tight">
                Latest Assessment
              </h3>
              {isAdmin && (
                <Link to={`/assessments/new?fighter=${id}`}>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700" data-testid="new-assessment-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    New Assessment
                  </Button>
                </Link>
              )}
            </div>
            {radarData ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#3f3f46" />
                    <PolarAngleAxis dataKey="skill" tick={{ fill: '#a1a1aa', fontSize: 12 }} />
                    <Radar
                      name="Skills"
                      dataKey="value"
                      stroke="#dc2626"
                      fill="#dc2626"
                      fillOpacity={0.3}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-zinc-500">
                <Trophy className="w-8 h-8 mb-2 opacity-50" />
                <p>No assessments recorded</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to={`/training-camps?fighter=${id}`}
            className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-sm hover:border-zinc-700 transition-colors flex items-center justify-between"
          >
            <span className="text-zinc-300">Training Camps</span>
            <ChevronRight className="w-5 h-5 text-zinc-600" />
          </Link>
          <Link
            to={`/assessments?fighter=${id}`}
            className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-sm hover:border-zinc-700 transition-colors flex items-center justify-between"
          >
            <span className="text-zinc-300">All Assessments</span>
            <ChevronRight className="w-5 h-5 text-zinc-600" />
          </Link>
          <Link
            to={`/fights?fighter=${id}`}
            className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-sm hover:border-zinc-700 transition-colors flex items-center justify-between"
          >
            <span className="text-zinc-300">Fight History</span>
            <ChevronRight className="w-5 h-5 text-zinc-600" />
          </Link>
        </div>

        {/* BJJ Belt Tracking Section */}
        <BeltTrackingCard 
          fighter={fighter} 
          isCoach={!isAdmin}
        />

        {/* BJJ Competition Records */}
        <CompetitionRecords 
          fighterId={id} 
          isCoach={isAdmin} 
        />

        {/* Fighter Notes Section */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm">
          <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MessageSquare className="w-5 h-5 text-blue-500" />
              <h3 className="font-heading text-base font-bold text-white uppercase tracking-tight">
                Notes & Communication
              </h3>
            </div>
            <span className="text-zinc-500 text-sm">{fighterNotes.length} notes</span>
          </div>
          
          {/* Send Note Form */}
          <div className="p-4 border-b border-zinc-800 bg-zinc-900/30">
            <div className="flex gap-3">
              <Textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Send a note to this fighter..."
                className="bg-zinc-950 border-zinc-800 min-h-[60px] resize-none flex-1"
                data-testid="coach-note-input"
              />
              <Button
                onClick={handleSendNote}
                disabled={sendingNote || !newNote.trim()}
                className="bg-red-600 hover:bg-red-700 self-end"
                data-testid="send-coach-note-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Notes List */}
          {fighterNotes.length > 0 ? (
            <div className="divide-y divide-zinc-800 max-h-96 overflow-y-auto">
              {fighterNotes.map((note) => (
                <div key={note.id} className="p-4" data-testid={`fighter-note-${note.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      note.author_role === 'fighter' ? 'bg-red-600/20' : 'bg-blue-600/20'
                    }`}>
                      <User className={`w-4 h-4 ${
                        note.author_role === 'fighter' ? 'text-red-500' : 'text-blue-500'
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white text-sm font-medium">{note.author_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-sm ${
                          note.author_role === 'fighter' 
                            ? 'bg-red-600/20 text-red-400' 
                            : 'bg-blue-600/20 text-blue-400'
                        }`}>
                          {note.author_role}
                        </span>
                        <span className="text-zinc-500 text-xs">
                          {new Date(note.created_at).toLocaleDateString('en-US', { 
                            month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' 
                          })}
                        </span>
                      </div>
                      <p className="text-zinc-300 text-sm mt-1">{note.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-zinc-500">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No notes yet</p>
              <p className="text-sm">Send a note to start the conversation</p>
            </div>
          )}
        </div>

        {/* Share Dialog */}
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-white uppercase tracking-tight flex items-center gap-2">
                <Share2 className="w-5 h-5 text-red-500" />
                Share Fighter Profile
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Create a public link to share {fighter?.name}'s profile with promoters and matchmakers.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              {shareStatus.share_enabled ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 bg-green-600/10 border border-green-600/30 rounded-sm">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-green-400 text-sm">Sharing is enabled</span>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-400 text-sm">Public Link</Label>
                    <div className="flex gap-2">
                      <Input
                        value={getShareUrl()}
                        readOnly
                        className="bg-zinc-800 border-zinc-700 text-zinc-300 text-sm"
                      />
                      <Button
                        onClick={handleCopyLink}
                        variant="outline"
                        size="icon"
                        className="border-zinc-700 hover:bg-zinc-800 flex-shrink-0"
                      >
                        {copied ? (
                          <Check className="w-4 h-4 text-green-500" />
                        ) : (
                          <Copy className="w-4 h-4 text-zinc-400" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <a
                    href={getShareUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-red-500 hover:text-red-400 text-sm transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Preview public profile
                  </a>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 p-3 bg-zinc-800 border border-zinc-700 rounded-sm">
                    <LinkIcon className="w-5 h-5 text-zinc-500" />
                    <span className="text-zinc-400 text-sm">Sharing is currently disabled</span>
                  </div>
                  
                  <p className="text-zinc-500 text-sm">
                    When you enable sharing, anyone with the link can view this fighter's public profile including their record, physical stats, and skill assessment.
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter className="flex gap-2">
              {shareStatus.share_enabled ? (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCopyLink}
                    className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDisableSharing}
                    disabled={shareLoading}
                    className="border-red-800 text-red-500 hover:bg-red-950"
                  >
                    {shareLoading ? 'Disabling...' : 'Disable Sharing'}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setShareDialogOpen(false)}
                    className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateShareLink}
                    disabled={shareLoading}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {shareLoading ? 'Creating...' : 'Enable Sharing'}
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-zinc-900 border-zinc-800">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Delete Fighter</AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                Are you sure you want to delete {fighter.name}? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-btn"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};
